import React, { useEffect, useRef, useState } from "react";
import { Play, Square, Sparkles, Mic2 } from "lucide-react";
import { playTick, audioClock } from "@/lib/audio";
import { stopTrack } from "@/lib/playalongEngine";
import { api } from "@/lib/api";

const BASE = process.env.REACT_APP_BACKEND_URL;
let indexPromise = null;
export const loadBacking = (fresh = false) => {
  if (!indexPromise || fresh) {
    indexPromise = Promise.all([
      fetch("/media/backing/index.json").then((r) => (r.ok ? r.json() : {})).catch(() => ({})),
      api.get("/backing").then(({ data }) => data.tracks).catch(() => ({})),
    ]).then(([stat, db]) => {
      const out = {};
      Object.entries(stat).forEach(([id, m]) => {
        out[id] = { full: { url: `/media/backing/${id}.mp3`, bpm: m.bpm, key: m.key, source: m.source || "ai", label: m.label || "AI recording" } };
      });
      Object.entries(db).forEach(([id, vs]) => {
        out[id] = { ...(out[id] || {}) };
        Object.entries(vs).forEach(([v, d]) => { out[id][v] = { ...out[id][v], ...d, url: `${BASE}${d.url}`, bpm: d.bpm || out[id][v]?.bpm, key: d.key || out[id][v]?.key }; });
      });
      return out;
    });
  }
  return indexPromise;
};
export const VARIANT_LABEL = { full: "Full band", "no-drums": "Without drums", "no-bass": "Without bass", "no-keys": "Without keys" };
export const pauseOthers = (e) => document.querySelectorAll("audio, video").forEach((m) => { if (m !== e.target) m.pause(); });
const SPEEDS = [0.75, 1];

export const StudioBand = ({ trackId, track, beats = 4, interactivePlaying, onPlayingChange }) => {
  const [rec, setRec] = useState(null);
  const [variant, setVariant] = useState("full");
  const [speed, setSpeed] = useState(1);
  const [state, setState] = useState("idle");
  const [count, setCount] = useState(null);
  const audio = useRef(null);
  const timers = useRef([]);
  useEffect(() => { loadBacking().then((idx) => { setRec(idx[trackId] || null); setVariant("full"); }); }, [trackId]);
  const clear = () => { timers.current.forEach(clearTimeout); timers.current = []; };
  const halt = () => { clear(); audio.current?.pause(); setCount(null); setState("idle"); };
  const stop = () => { halt(); onPlayingChange?.(false); };
  useEffect(() => stop, [trackId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (interactivePlaying) halt(); }, [interactivePlaying]); // eslint-disable-line react-hooks/exhaustive-deps
  const cur = rec?.[variant] || rec?.full;
  const play = () => {
    stopTrack();
    const a = audio.current;
    a.currentTime = 0;
    a.preservesPitch = true;
    a.playbackRate = speed;
    const beat = 60 / ((cur.bpm || track.bpm) * speed), n = Math.round(beats), ac = audioClock(), T = ac.currentTime + 0.1;
    for (let b = 0; b < n; b += 1) {
      playTick(b === 0 ? 1320 : 990, 0.06, 1, T + b * beat);
      timers.current.push(setTimeout(() => setCount(b + 1), (0.1 + b * beat) * 1000));
    }
    timers.current.push(setTimeout(() => { setCount(null); a.play(); setState("playing"); }, (0.1 + n * beat) * 1000));
    setState("countin");
    onPlayingChange?.(true);
  };
  const pickSpeed = (r) => { setSpeed(r); if (audio.current) { audio.current.preservesPitch = true; audio.current.playbackRate = r; } };
  if (!cur) return null;
  const jason = cur.source !== "ai";
  return (
    <div className="mt-5 rounded-2xl border border-primary/40 bg-primary/5 p-4" data-testid="studio-band">
      <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-primary" data-testid="studio-band-label">
        {jason ? <Mic2 size={13} /> : <Sparkles size={13} />} Studio band · {cur.label.replace("Studio band ", "").replace(/[()]/g, "")}
      </p>
      <p className="mt-1 text-sm text-muted-foreground">
        {jason ? "A real recording by Jason's band" : "A realistic full-band recording in the style of this track"} ({cur.bpm || track.bpm} bpm{cur.key ? `, key of ${cur.key.replace("b", "♭")}` : ""}).
      </p>
      <div className="mt-3 grid gap-2 text-xs sm:grid-cols-2" data-testid="studio-band-modes">
        <p className="rounded-xl border border-primary/40 bg-background p-3" data-testid="studio-band-mode-listen">
          <span className="block font-semibold text-primary">Listen & jam (this player)</span>
          Plays the recording on its own. The chord chart won't follow along{jason ? "" : ", and the AI band may not match it bar for bar"}.
        </p>
        <p className="rounded-xl border border-border bg-background p-3" data-testid="studio-band-mode-practice">
          <span className="block font-semibold text-foreground">Practice with the chart (interactive band below)</span>
          Highlights each bar as it plays. Change the tempo or key and mute parts there.
        </p>
      </div>
      {Object.keys(rec).length > 1 && (
        <div className="mt-3 flex flex-wrap gap-2" data-testid="studio-band-variants">
          {Object.keys(VARIANT_LABEL).filter((v) => rec[v]).map((v) => (
            <button key={v} type="button" onClick={() => { halt(); setVariant(v); }} data-testid={`studio-band-variant-${v}`}
              className={`rounded-full px-3 py-1 text-xs font-semibold ${variant === v ? "bg-primary text-primary-foreground" : "border border-border hover:border-primary"}`}>{VARIANT_LABEL[v]}</button>
          ))}
        </div>
      )}
      <div className="mt-3 flex flex-wrap items-center gap-3">
        {state === "idle" ? (
          <button type="button" onClick={play} data-testid="studio-band-play" className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2 text-sm font-semibold text-primary-foreground transition-transform hover:scale-105"><Play size={15} /> Play studio band</button>
        ) : (
          <button type="button" onClick={stop} data-testid="studio-band-stop" className="inline-flex items-center gap-2 rounded-full border border-primary px-5 py-2 text-sm font-semibold text-primary"><Square size={15} /> Stop</button>
        )}
        <span className="flex items-center gap-1.5 text-xs text-muted-foreground">Speed:
          {SPEEDS.map((r) => (
            <button key={r} type="button" onClick={() => pickSpeed(r)} data-testid={`studio-band-speed-${String(r).replace(".", "_")}`}
              className={`rounded-full px-2.5 py-0.5 font-semibold ${speed === r ? "bg-primary text-primary-foreground" : "border border-border hover:border-primary"}`}>{r}×</button>
          ))}
        </span>
        {count && <span className="font-serif text-3xl text-primary" data-testid="studio-band-count">{count}</span>}
        {state === "playing" && <span className="text-xs text-muted-foreground" data-testid="studio-band-status">Playing…</span>}
      </div>
      <audio ref={audio} src={cur.url} preload="none" onEnded={stop} data-testid="studio-band-audio" />
    </div>
  );
};
